import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../../components/feature/PageSEO';

const HomePage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [openInternationalFAQ, setOpenInternationalFAQ] = useState<number | null>(null);
  const [openQuickFAQ, setOpenQuickFAQ] = useState<number | null>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const testimonials = [
    {
      initials: "D.C.",
      name: "Daphne Chang",
      service: "Service: Consulate-Ready Passport Photos",
      review: "Mr. Roberts is incredibly kind, patient, and meticulous. I came in for passport photos, which can be a stressful errand, but he guided me through everything and made sure every detail was perfect. The results were excellent! My photos were accepted by my consulate without any issues.",
      tag: "Verified Google Review | 5 Stars"
    },
    {
      initials: "T.M.",
      name: "Tim McGrail",
      service: "Service: Canadian Passport Photo",
      review: "I went to the store for a Canadian passport photo, which was handled quickly and professionally, with knowledge of the specific requirements for each country's passports. Come for the prompt, professional photography service, stay for the hospitality and friendliness of the business owner.",
      tag: "Verified Google Review | 5 Stars"
    },
    {
      initials: "M.",
      name: "Michelle",
      service: "Service: Professional Portrait & Passport",
      review: "The Post Office employee helping me with my passport application remarked that these were the most perfect passport photos he had ever seen. Mr. Roberts is a longtime professional photographer with a great range of skills—he even lit our family members with different skin tones perfectly!",
      tag: "Verified Google Review | 5 Stars"
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleVideoPlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setVideoPlaying(true);
    }
  };

  const internationalFAQs = [
    {
      question: "What are the Canadian passport photo requirements?",
      snippet: "Canadian passport photos require exact 50mm x 70mm dimensions with specific head-to-frame ratios set by IRCC.",
      answer: "Immigration, Refugees and Citizenship Canada (IRCC) mandates precise specifications that drugstore kiosks frequently fail to meet. Photos must be 50mm x 70mm with the face measuring between 31mm and 36mm from chin to crown. We use professional biometric verification software to ensure your Canadian passport photo meets all IRCC standards, including the guarantor signature area on printed copies. Our 53 years of experience means we understand these nuances perfectly."
    },
    {
      question: "Do you take UK biometric passport photos in Boston?",
      snippet: "Yes—we specialize in UK passport photos meeting HM Passport Office's strict 45mm x 35mm biometric standards.",
      answer: "UK passport photos have among the strictest requirements globally. HM Passport Office requires exact 45mm x 35mm dimensions, neutral expression, and specific head positioning for facial recognition systems. Our professional studio lighting eliminates the harsh shadows and glare that cause rejections. We've helped hundreds of British expats and dual citizens across Greater Boston with their UK passport renewals—all with 100% acceptance."
    },
    {
      question: "Can you produce Jamaican visa and passport photos?",
      snippet: "Absolutely—we create Jamaican passport photos meeting all PICA specifications.",
      answer: "Applications require 35mm x 45mm photos with a matte/dull finish. Unlike many other countries, Jamaica requires specific background colors based on your complexion: white for dark skin tones and pastel or pale blue for light skin or grey hair. We provide high-resolution digital files for online renewals and professionally printed sets, ensuring your head-to-margin ratio is exactly within the requirement. Please inquire about our full application service."
    }
  ];

  const quickAnswerFAQs = [
    {
      question: "Why choose a professional studio over a drugstore for passport photos?",
      snippet: "Professional studios offer expert lighting, guaranteed acceptance, and photos you'll be proud of for 10 years.",
      answer: "Drugstore kiosks use harsh fluorescent lighting, untrained staff, and automated systems that produce unflattering results. At Roberts Photo Studio, you get professional studio lighting that flatters all skin tones, an experienced photographer who understands exact requirements for 190+ countries, complimentary digital retouching, and our ironclad 100% acceptance guarantee. Your passport photo represents you for up to 10 years—make it count."
    },
    {
      question: "How long has Roberts Photo Studio been serving Greater Boston?",
      snippet: "We've been Boston's trusted passport photo specialists since 1973—over 50 years of professional expertise.",
      answer: "Roberts Photo Studio has proudly served the Greater Boston community for 53 years from our Dorchester location at 840 Morton Street. Three generations of Boston families have trusted us with their passport photos. Our longevity speaks to our commitment to quality, customer service, and staying current with ever-changing international photo requirements. When you choose us, you're choosing a neighborhood institution with an unmatched track record."
    },
    {
      question: "Do you guarantee my passport photo will be accepted?",
      snippet: "Yes—we offer a 100% acceptance guarantee on all passport photos, backed by 53 years of expertise.",
      answer: "Every passport photo we produce comes with our ironclad 100% acceptance guarantee. If your photo is ever rejected by any passport agency or consulate (which hasn't happened in years), we'll reshoot it completely free of charge. Our professional equipment, biometric verification process, and decades of experience ensure your photos meet all requirements the first time. Customers from Quincy, Cambridge, Brookline, Newton, Milton, and Braintree trust this guarantee."
    }
  ];

  const scrollToTop = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const w = window as any;
    const initCal = () => {
      if (!w.Cal) {
        const C = w;
        const A = 'https://app.cal.com/embed/embed.js';
        const L = 'init';
        const p = (a: any, ar: any) => { a.q.push(ar); };
        const d = C.document;
        C.Cal = C.Cal || function (...args: any[]) {
          const cal = C.Cal;
          const ar = args;
          if (!cal.loaded) {
            cal.ns = {};
            cal.q = cal.q || [];
            const s = d.head.appendChild(d.createElement('script'));
            s.src = A;
            cal.loaded = true;
          }
          if (ar[0] === L) {
            const api = function (...apiArgs: any[]) { p(api, apiArgs); };
            const namespace = ar[1];
            api.q = api.q || [];
            if (typeof namespace === 'string') {
              cal.ns[namespace] = cal.ns[namespace] || api;
              p(cal.ns[namespace], ar);
              p(cal, ['initNamespace', namespace]);
            } else {
              p(cal, ar);
            }
            return;
          }
          p(cal, ar);
        };
      }
      w.Cal('init', 'passport-photo-appointment', { origin: 'https://app.cal.com' });
      w.Cal.ns['passport-photo-appointment']('inline', {
        elementOrSelector: '#my-cal-inline-passport-photo-appointment',
        config: { layout: 'month_view', useSlotsViewOnSmallScreen: 'true' },
        calLink: 'roberts-photo-studio/passport-photo-appointment',
      });
      w.Cal.ns['passport-photo-appointment']('ui', { hideEventTypeDetails: false, layout: 'month_view' });
    };
    initCal();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <PageSEO
        pageId="home"
        canonical="https://mybostonpassportphotos.com/"
        title="Passport Photos Boston | Roberts Photo Studio | Guaranteed Acceptance"
        description="Professional US & international passport photos in Boston since 1973. 100% acceptance guarantee. Walk-ins welcome at Roberts Photo Studio, 840 Morton St, Dorchester."
        openGraph={{
          title: "Passport Photos Boston | Roberts Photo Studio | Guaranteed Acceptance",
          description: "Professional US & international passport photos in Boston since 1973. 100% acceptance guarantee. Walk-ins welcome at 840 Morton St, Dorchester. Jamaica, UK, Canada, and 190+ countries.",
          image: "https://static.readdy.ai/image/6521ef01a44cd5c540b1d9b66db907e8/ce36848917375a093a2c974e9976de97.jpeg",
          imageAlt: "Roberts Photo Studio – Professional Passport Photos in Boston, MA since 1973",
          imageWidth: 1200,
          imageHeight: 630,
          url: "https://mybostonpassportphotos.com/",
          type: "website",
          siteName: "Roberts Photo Studio",
        }}
        hreflang={[
          { hreflang: 'en', href: 'https://mybostonpassportphotos.com/' },
          { hreflang: 'x-default', href: 'https://mybostonpassportphotos.com/' },
        ]}
        schema={[
          // ── 1. WebPage ────────────────────────────────────────────────────
          // LocalBusiness + WebSite are defined in index.html — no duplicates here.
          // All @id cross-references below resolve to those static anchor nodes.
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://mybostonpassportphotos.com/#webpage",
            "url": "https://mybostonpassportphotos.com/",
            "name": "Passport Photos Boston | Roberts Photo Studio | Guaranteed Acceptance",
            "description": "Professional US & International passport photos in Boston since 1973. 100% acceptance guarantee. Walk-ins welcome at Roberts Photo Studio, Dorchester.",
            "isPartOf": { "@id": "https://mybostonpassportphotos.com/#website" },
            "about": { "@id": "https://mybostonpassportphotos.com/#business" },
            "inLanguage": "en-US",
            "dateModified": "2026-04-12",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mybostonpassportphotos.com/" }
              ]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "@id": "https://mybostonpassportphotos.com/#faqpage",
            "isPartOf": { "@id": "https://mybostonpassportphotos.com/#website" },
            "about": { "@id": "https://mybostonpassportphotos.com/#business" },
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What are the Canadian passport photo requirements?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Canadian passport photos require exact 50mm x 70mm dimensions with the face measuring between 31mm and 36mm from chin to crown. We use professional biometric verification software to ensure your Canadian passport photo meets all IRCC standards."
                }
              },
              {
                "@type": "Question",
                "name": "Do you take UK biometric passport photos in Boston?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes—we specialize in UK passport photos meeting HM Passport Office's strict 45mm x 35mm biometric standards with 100% acceptance."
                }
              },
              {
                "@type": "Question",
                "name": "Can you produce Jamaican visa and passport photos?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely—we create Jamaican passport photos meeting all PICA specifications. Applications require 35mm x 45mm photos with a matte/dull finish and complexion-matched backgrounds."
                }
              },
              {
                "@type": "Question",
                "name": "Why choose a professional studio over a drugstore for passport photos?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Professional studios offer expert lighting, guaranteed acceptance, and photos you'll be proud of for 10 years. Roberts Photo Studio provides professional studio lighting, biometric verification, and an ironclad 100% acceptance guarantee."
                }
              },
              {
                "@type": "Question",
                "name": "Do you guarantee my passport photo will be accepted?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes—we offer a 100% acceptance guarantee on all passport photos, backed by 53 years of expertise. If your photo is ever rejected, we'll reshoot it completely free of charge."
                }
              }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": "Jamaican Passport Photo Process – Roberts Photo Studio Boston",
            "description": "Watch how Roberts Photo Studio in Dorchester, Boston produces PICA-compliant Jamaican passport photos. We specialize in 35mm x 45mm matte-finish photos with complexion-matched backgrounds, meeting all Jamaica Passport, Immigration and Citizenship Agency (PICA) specifications. Serving Boston's Caribbean community since 1973.",
            "thumbnailUrl": "https://static.readdy.ai/image/6521ef01a44cd5c540b1d9b66db907e8/52e33329bfb1d7e04befb459098acf93.jpeg",
            "uploadDate": "2025-01-15",
            "contentUrl": "https://res.cloudinary.com/duyiyepjg/video/upload/v1771872594/VIDEO-2025-01-15-17-48-41_nd3scx.mov",
            "embedUrl": "https://mybostonpassportphotos.com/#jamaica-video",
            "duration": "PT1M",
            "publisher": {
              "@id": "https://mybostonpassportphotos.com/#business"
            },
            "author": {
              "@id": "https://mybostonpassportphotos.com/#business"
            },
            "inLanguage": "en-US",
            "keywords": "Jamaican passport photo Boston, PICA passport photo, Jamaica passport photo requirements, biometric passport photo Dorchester",
            "about": {
              "@type": "Service",
              "name": "Jamaican Passport Photo Service",
              "provider": { "@id": "https://mybostonpassportphotos.com/#business" }
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Passport Photography",
            "provider": { "@id": "https://mybostonpassportphotos.com/#business" },
            "areaServed": { "@type": "City", "name": "Boston", "addressRegion": "MA", "addressCountry": "US" },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Passport Photo Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": { "@type": "Service", "name": "Standard US Passport Photo", "description": "Professional US passport photos with guaranteed USPS acceptance. Includes 2 printed photos." },
                  "price": "30.00",
                  "priceCurrency": "USD"
                },
                {
                  "@type": "Offer",
                  "itemOffered": { "@type": "Service", "name": "International & Biometric Passport Photo", "description": "Biometric passport photos for Jamaica, Haiti, Barbados, UK, Canada and 190+ countries. Includes 2 printed photos." },
                  "price": "50.00",
                  "priceCurrency": "USD"
                }
              ]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "@id": "https://mybostonpassportphotos.com/#product-us-passport-photo",
            "name": "Standard US Passport Photo – Boston",
            "description": "Professional US passport photos taken at Roberts Photo Studio in Dorchester, Boston. Includes 2 printed 2x2 inch photos meeting all State Department requirements. Guaranteed acceptance at USPS, post offices, and passport agencies. Same-day service, walk-ins welcome.",
            "image": "https://static.readdy.ai/image/6521ef01a44cd5c540b1d9b66db907e8/ce36848917375a093a2c974e9976de97.jpeg",
            "brand": { "@id": "https://mybostonpassportphotos.com/#business" },
            "category": "Passport Photography Services",
            "keywords": "US passport photo Boston, passport photo price Boston, 2x2 passport photo Dorchester, same day passport photo Boston",
            "offers": {
              "@type": "Offer",
              "@id": "https://mybostonpassportphotos.com/#offer-us-passport-photo",
              "url": "https://mybostonpassportphotos.com/#pricing",
              "priceCurrency": "USD",
              "price": "30.00",
              "priceValidUntil": "2026-12-31",
              "availability": "https://schema.org/InStock",
              "itemCondition": "https://schema.org/NewCondition",
              "seller": { "@id": "https://mybostonpassportphotos.com/#business" },
              "areaServed": {
                "@type": "City",
                "name": "Boston",
                "addressRegion": "MA",
                "addressCountry": "US"
              },
              "hasMerchantReturnPolicy": {
                "@type": "MerchantReturnPolicy",
                "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                "merchantReturnDays": 30,
                "returnMethod": "https://schema.org/ReturnInStore",
                "returnFees": "https://schema.org/FreeReturn"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5",
              "reviewCount": "3",
              "bestRating": "5",
              "worstRating": "1"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "@id": "https://mybostonpassportphotos.com/#product-international-passport-photo",
            "name": "International & Biometric Passport Photo – Boston",
            "description": "Professional international and biometric passport photos at Roberts Photo Studio, Dorchester, Boston. Covers Jamaica (PICA-compliant, 35x45mm), Canada (IRCC 50x70mm), UK (HM Passport Office 45x35mm), Barbados, Haiti, and 190+ countries. Includes 2 printed photos with complexion-matched backgrounds. 100% acceptance guarantee. Digital files available.",
            "image": "https://static.readdy.ai/image/6521ef01a44cd5c540b1d9b66db907e8/ce36848917375a093a2c974e9976de97.jpeg",
            "brand": { "@id": "https://mybostonpassportphotos.com/#business" },
            "category": "Passport Photography Services",
            "keywords": "international passport photo Boston, Jamaican passport photo price Boston, Canadian passport photo Boston, biometric passport photo Dorchester, UK passport photo Boston",
            "offers": {
              "@type": "Offer",
              "@id": "https://mybostonpassportphotos.com/#offer-international-passport-photo",
              "url": "https://mybostonpassportphotos.com/#pricing",
              "priceCurrency": "USD",
              "price": "50.00",
              "priceValidUntil": "2026-12-31",
              "availability": "https://schema.org/InStock",
              "itemCondition": "https://schema.org/NewCondition",
              "seller": { "@id": "https://mybostonpassportphotos.com/#business" },
              "areaServed": {
                "@type": "City",
                "name": "Boston",
                "addressRegion": "MA",
                "addressCountry": "US"
              },
              "hasMerchantReturnPolicy": {
                "@type": "MerchantReturnPolicy",
                "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                "merchantReturnDays": 30,
                "returnMethod": "https://schema.org/ReturnInStore",
                "returnFees": "https://schema.org/FreeReturn"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5",
              "reviewCount": "3",
              "bestRating": "5",
              "worstRating": "1"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "@id": "https://mybostonpassportphotos.com/#product-digital-passport-photo",
            "name": "Digital Passport Photo File – Boston",
            "description": "High-resolution digital passport photo file from Roberts Photo Studio, Boston. Delivered as a 600x600 DPI digital file suitable for online passport renewals including PICA (Jamaica), IRCC (Canada), and US State Department online applications. Includes professional retouching and biometric compliance verification.",
            "image": "https://static.readdy.ai/image/6521ef01a44cd5c540b1d9b66db907e8/ce36848917375a093a2c974e9976de97.jpeg",
            "brand": { "@id": "https://mybostonpassportphotos.com/#business" },
            "category": "Passport Photography Services",
            "keywords": "digital passport photo Boston, online passport photo file Boston, PICA digital photo Boston, 600 DPI passport photo",
            "offers": {
              "@type": "Offer",
              "@id": "https://mybostonpassportphotos.com/#offer-digital-passport-photo",
              "url": "https://mybostonpassportphotos.com/#pricing",
              "priceCurrency": "USD",
              "price": "75.00",
              "priceValidUntil": "2026-12-31",
              "availability": "https://schema.org/InStock",
              "itemCondition": "https://schema.org/NewCondition",
              "seller": { "@id": "https://mybostonpassportphotos.com/#business" },
              "areaServed": {
                "@type": "City",
                "name": "Boston",
                "addressRegion": "MA",
                "addressCountry": "US"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5",
              "reviewCount": "3",
              "bestRating": "5",
              "worstRating": "1"
            }
          }
        ]}
      />
      {/* Navigation */}
      <nav className="fixed w-full top-0 z-50 transition-all duration-300 bg-black backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center space-x-2 sm:space-x-3 cursor-pointer min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#DC143C] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-camera-2-line text-[#FFD700] text-lg sm:text-xl"></i>
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-sm sm:text-lg md:text-xl font-bold text-white truncate leading-tight">Roberts Photo Studio</span>
                <span className="text-[#FFD700] text-[9px] sm:text-xs font-semibold tracking-wide truncate leading-tight">Boston&apos;s Passport Photo Specialists</span>
              </div>
            </Link>

            {/* Phone Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Link to="/faq" className="text-white font-bold hover:text-[#FFD700] transition-colors">FAQs</Link>
              <a href="tel:617-288-4417" className="text-[#FFD700] font-bold bg-[#DC143C] px-4 py-2 rounded-full hover:bg-[#B71C1C] transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line mr-2"></i>(617) 288-4417
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-white text-2xl w-11 h-11 flex items-center justify-center cursor-pointer flex-shrink-0"
            >
              <i className={isMenuOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden mt-4 bg-white/95 backdrop-blur rounded-lg p-4">
              <div className="flex flex-col space-y-1">
                <a href="#caribbean" onClick={() => setIsMenuOpen(false)} className="text-gray-800 hover:text-[#DC143C] font-medium py-3 min-h-[48px] flex items-center px-2 rounded-lg hover:bg-gray-100 transition-colors">International Passports</a>
                <a href="#pricing" onClick={() => setIsMenuOpen(false)} className="text-gray-800 hover:text-[#DC143C] font-medium py-3 min-h-[48px] flex items-center px-2 rounded-lg hover:bg-gray-100 transition-colors">Pricing</a>
                <Link to="/faq" onClick={() => setIsMenuOpen(false)} className="text-gray-800 hover:text-[#DC143C] font-medium py-3 min-h-[48px] flex items-center px-2 rounded-lg hover:bg-gray-100 transition-colors">FAQs</Link>
                <a href="#book" onClick={() => setIsMenuOpen(false)} className="text-gray-800 hover:text-[#DC143C] font-medium py-3 min-h-[48px] flex items-center px-2 rounded-lg hover:bg-gray-100 transition-colors">Book Online</a>
                <a href="tel:617-288-4417" onClick={() => setIsMenuOpen(false)} className="text-[#FFD700] font-bold bg-[#DC143C] py-3 min-h-[48px] flex items-center justify-center px-4 rounded-full mt-2 cursor-pointer">
                  <i className="ri-phone-line mr-2"></i>(617) 288-4417
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* LCP hint: hidden img forces fetchpriority=high in browsers that don't honour CSS background preload */}
        <img
          src="https://public.readdy.ai/ai/img_res/38ae2045f0399a413b9efa14325d3620.jpg"
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
          className="absolute w-0 h-0 overflow-hidden opacity-0 pointer-events-none"
        />
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=stunning%20Caribbean%20beach%20at%20golden%20hour%20with%20turquoise%20water%20white%20sand%20palm%20trees%20tropical%20paradise%20professional%20photography%20vibrant%20colors%20warm%20lighting%20peaceful%20atmosphere&width=1920&height=1080&seq=4&orientation=landscape')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        
        <div className="relative z-10 text-center max-w-4xl px-4 sm:px-6 w-full py-12 sm:py-20">
          <div className="mb-4 sm:mb-6">
            <span className="text-[#FFD700] text-[10px] sm:text-sm font-bold uppercase tracking-widest bg-[#DC143C] px-3 sm:px-4 py-2 rounded-full backdrop-blur-sm inline-block">
              Boston&apos;s Passport Specialists Since 1973
            </span>
          </div>
          
          <h1 className="text-[28px] leading-tight sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 sm:mb-6 px-2">
            Your Passport is Your<br />
            Travel Keepsake.<br />
            <span className="font-normal text-xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl block mt-2">
              <span className="text-[#FFD700] font-bold">Make it a </span>
              <span className="text-[#DC143C] font-bold">Portrait You&apos;re Proud of.</span>
            </span>
          </h1>
          
          <p className="text-sm sm:text-xl md:text-2xl text-[#FAF6F1] mb-6 sm:mb-8 max-w-3xl mx-auto font-medium leading-relaxed px-2">
            Professional studio for US and International passports.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
            <a href="#book" className="bg-[#DC143C] text-[#FFD700] px-8 sm:px-12 py-4 rounded-full text-base sm:text-lg font-bold hover:bg-[#B71C1C] transition-all duration-300 hover:shadow-lg hover:shadow-[#FFD700]/20 whitespace-nowrap min-h-[56px] flex items-center justify-center">
              Book Your Studio Session
            </a>
          </div>
        </div>
      </section>

      {/* Studio Difference Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-[#FFD700] relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/6521ef01a44cd5c540b1d9b66db907e8/52b90c818b8b7411efb9532891b6c686.jpeg')`
          }}
        ></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#DC143C] mb-4">The Studio Difference</h2>
            <div className="w-20 h-1 bg-black mb-4"></div>
            <p className="text-sm sm:text-lg text-black font-bold">Why a professional studio matters for your passport portrait</p>
          </div>

          <div className="max-w-5xl mx-auto mb-8 sm:mb-12">
            {/* Roberts Studio */}
            <div className="bg-red-50 rounded-2xl sm:rounded-3xl p-4 sm:p-8 md:p-12 shadow-2xl border-4 border-black mb-8 relative">
              <div className="mb-6 sm:mb-8">
                <img 
                  src="https://static.readdy.ai/image/6521ef01a44cd5c540b1d9b66db907e8/8074c4342baa0f1bc63eb293c3463273.jpeg"
                  alt="Professional studio photo"
                  className="w-full h-48 sm:h-64 md:h-80 object-cover rounded-xl"
                />
              </div>
              
              {/* Two Column Comparison */}
              <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                {/* Roberts Photo Studio - Left Column */}
                <div>
                  <h3 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">Roberts Photo Studio Experience</h3>
                  <ul className="space-y-3 sm:space-y-4">
                    <li className="flex items-center text-gray-700 font-bold text-sm sm:text-base">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <i className="ri-check-line text-white text-base sm:text-lg"></i>
                      </div>
                      Professional lighting
                    </li>
                    <li className="flex items-center text-gray-700 font-bold text-sm sm:text-base">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <i className="ri-check-line text-white text-base sm:text-lg"></i>
                      </div>
                      Flattering angles &amp; posing guidance
                    </li>
                    <li className="flex items-center text-gray-700 font-bold text-sm sm:text-base">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <i className="ri-check-line text-white text-base sm:text-lg"></i>
                      </div>
                      Guaranteed acceptance
                    </li>
                  </ul>
                </div>

                {/* Drugstore Kiosk - Right Column */}
                <div className="md:border-l-2 md:border-gray-200 md:pl-10">
                  <h4 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">Drugstore Kiosk</h4>
                  <ul className="space-y-3 sm:space-y-4">
                    <li className="flex items-center text-gray-700 font-bold text-sm sm:text-base">
                      <i className="ri-close-circle-line text-red-500 text-xl sm:text-2xl mr-3 flex-shrink-0"></i>
                      Harsh overhead lighting
                    </li>
                    <li className="flex items-center text-gray-700 font-bold text-sm sm:text-base">
                      <i className="ri-close-circle-line text-red-500 text-xl sm:text-2xl mr-3 flex-shrink-0"></i>
                      Unflattering shadows
                    </li>
                    <li className="flex items-center text-gray-700 font-bold text-sm sm:text-base">
                      <i className="ri-close-circle-line text-red-500 text-xl sm:text-2xl mr-3 flex-shrink-0"></i>
                      One-size-fits-all approach
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center px-4">
            <div className="inline-block bg-[#FFD700] backdrop-blur-sm px-6 sm:px-12 md:px-16 py-6 sm:py-8 rounded-full border-4 border-black mx-auto max-w-xl">
              <p className="text-sm sm:text-lg md:text-xl lg:text-2xl italic text-[#DC143C] font-bold">
                Your passport photo represents you and your travel adventures. Make it count.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* International Passport & Visa Services Section */}
      <section id="caribbean" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-[#0A1F44] to-[#1a3a5c] relative overflow-hidden">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full bg-repeat" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFD700' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM0 6v4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM6 34v-4H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative">
          {/* Header Section */}
          <div className="text-center mb-12 sm:mb-16 md:mb-20">
            <div className="mb-4">
              <span className="text-[#FFD700] text-[10px] sm:text-sm font-bold uppercase tracking-widest bg-white/10 px-3 sm:px-4 py-2 rounded-full backdrop-blur-sm">
                Since 1973 • Boston&apos;s Trusted Experts
              </span>
            </div>
            <h2 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 px-2 leading-tight">
              International Passport &amp; Visa Photos in Boston:<br className="hidden sm:block" />
              <span className="text-[#FFD700]">Guaranteed Global Acceptance</span>
            </h2>
            <p className="text-sm sm:text-lg md:text-xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              Skip the drugstore guesswork. Since 1973, we&apos;ve been Boston&apos;s premier studio for precision biometric photos for Jamaica, Haiti, Barbados, the UK, and 190+ countries.
            </p>
          </div>

          {/* Why Us Cards */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8 mb-12 sm:mb-16 md:mb-20">
            {/* Card 1 */}
            <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-2xl hover:shadow-[#FFD700]/20 transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#0A1F44] to-[#1a3a5c] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-global-line text-[#FFD700] text-2xl sm:text-3xl"></i>
              </div>
              <h3 className="text-lg sm:text-2xl font-bold text-[#0A1F44] mb-3 sm:mb-4">Global Biometric Experts</h3>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                We utilize the <strong className="text-[#0A1F44]">&apos;Biometric Technology&apos;</strong> to cross-reference every photo against current embassy requirements for every nation, including the UK, Canada, and the EU.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-2xl hover:shadow-[#FFD700]/20 transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#0A1F44] to-[#1a3a5c] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-map-pin-user-line text-[#FFD700] text-2xl sm:text-3xl"></i>
              </div>
              <h3 className="text-lg sm:text-2xl font-bold text-[#0A1F44] mb-3 sm:mb-4">Caribbean &amp; African Specialists</h3>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                The go-to studio for Boston&apos;s international community. We specialize in <strong className="text-[#0A1F44]">high-resolution digital standards for Jamaica (600x600 DPI)</strong> and Barbados, ensuring your online PICA filings are never rejected.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-2xl hover:shadow-[#FFD700]/20 transition-all duration-300 hover:-translate-y-1 sm:col-span-2 md:col-span-1">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#0A1F44] to-[#1a3a5c] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-image-edit-line text-[#FFD700] text-2xl sm:text-3xl"></i>
              </div>
              <h3 className="text-lg sm:text-2xl font-bold text-[#0A1F44] mb-3 sm:mb-4">Precision Digital Delivery</h3>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                We don&apos;t just crop; we <strong className="text-[#0A1F44]">manually enhance every digital file</strong> to meet exact pixel, DPI, and file-size specifications for instant online upload.
              </p>
            </div>
          </div>

          {/* Jamaica Specialist Card */}
          <div className="mb-12 sm:mb-16 md:mb-20">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border-2 border-[#FFD700]/30">
              {/* Video Section - Full Width Top */}
              <div className="relative bg-[#0A1F44] p-3 sm:p-6 md:p-8">
                <div className="flex flex-col items-center gap-2 mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#FFD700] rounded-full flex items-center justify-center">
                    <i className="ri-play-circle-line text-[#0A1F44] text-xl sm:text-3xl"></i>
                  </div>
                  <div className="text-center">
                    <span className="text-[#FFD700] text-lg sm:text-2xl md:text-3xl font-bold uppercase tracking-widest">Watch &amp; Learn</span>
                    <p className="text-gray-400 text-xs sm:text-base mt-1">See our Jamaican passport photo process</p>
                  </div>
                </div>
                <div className="w-full aspect-video bg-black rounded-xl overflow-hidden shadow-2xl ring-2 ring-[#FFD700]/20 relative">
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    controls
                    playsInline
                    preload="metadata"
                    poster="https://static.readdy.ai/image/6521ef01a44cd5c540b1d9b66db907e8/52e33329bfb1d7e04befb459098acf93.jpeg"
                    onPlay={() => setVideoPlaying(true)}
                  >
                    <source src="https://res.cloudinary.com/duyiyepjg/video/upload/v1771872594/VIDEO-2025-01-15-17-48-41_nd3scx.mov" type="video/quicktime" />
                    <source src="https://res.cloudinary.com/duyiyepjg/video/upload/v1771872594/VIDEO-2025-01-15-17-48-41_nd3scx.webm" type="video/webm" />
                    Your browser does not support the video tag.
                  </video>
                  {/* Large Play Button Overlay - hidden once playing */}
                  {!videoPlaying && (
                    <button
                      onClick={handleVideoPlay}
                      className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/10"
                      aria-label="Play video"
                    >
                      <div className="w-16 h-16 sm:w-24 sm:h-24 bg-white/90 rounded-full flex items-center justify-center shadow-2xl">
                        <i className="ri-play-fill text-[#009B3A] text-3xl sm:text-5xl ml-1"></i>
                      </div>
                    </button>
                  )}
                </div>
              </div>

              {/* Info Section Below Video */}
              <div className="p-4 sm:p-8 lg:p-10">
                <div className="flex flex-col items-center gap-3 mb-6">
                  <div className="text-center">
                    <h3 className="text-xl sm:text-3xl font-bold text-[#0A1F44]">Jamaica Passport Specialists</h3>
                    <p className="text-[#DC143C] font-semibold text-xs sm:text-sm">Boston&apos;s #1 PICA-Compliant Studio</p>
                  </div>
                </div>

                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-6 text-center">
                  Applications require <strong className="text-[#0A1F44]">35mm x 45mm photos with a matte/dull finish</strong>. Unlike many other countries, Jamaica requires specific background colors based on your complexion: <strong className="text-[#DC143C]">white for dark skin tones</strong> and <strong className="text-[#DC143C]">pastel or pale blue for light skin or grey hair</strong>. Please inquire about our full application service.
                </p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-gray-700 text-xs sm:text-sm"><strong className="text-[#0A1F44]">Professional passport photos</strong> meeting all PICA specifications</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-gray-700 text-xs sm:text-sm"><strong className="text-[#0A1F44]">Passport application assistance</strong> to guide you through the process</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-gray-700 text-xs sm:text-sm"><strong className="text-[#0A1F44]">Green card application assistance</strong> for immigration needs</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-gray-700 text-xs sm:text-sm"><strong className="text-[#0A1F44]">In-house notary services</strong> for document authentication</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-gray-700 text-xs sm:text-sm"><strong className="text-[#0A1F44]">600x600 DPI digital files</strong> for online PICA renewals</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-gray-700 text-xs sm:text-sm"><strong className="text-[#0A1F44]">Complexion-matched backgrounds</strong> per PICA guidelines</p>
                  </div>
                </div>

                <div className="bg-[#FAF6F1] rounded-xl p-4 mb-6 mx-auto max-w-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-shield-check-line text-[#DC143C] text-lg"></i>
                    <span className="text-[#0A1F44] font-bold text-sm">100% Acceptance Guarantee</span>
                  </div>
                  <p className="text-gray-600 text-xs sm:text-sm">Every Jamaican passport photo we produce is backed by our ironclad guarantee. If PICA rejects your photo, we reshoot for free.</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a href="#book" className="bg-[#DC143C] text-[#FFD700] px-6 py-4 rounded-full text-sm sm:text-base font-bold hover:bg-[#B71C1C] transition-all duration-300 text-center whitespace-nowrap min-h-[56px] flex items-center justify-center">
                    Book Jamaica Session
                  </a>
                  <a href="tel:617-288-4417" className="bg-[#0A1F44] text-white px-6 py-4 rounded-full text-sm sm:text-base font-bold hover:bg-[#1a3a5c] transition-all duration-300 text-center whitespace-nowrap cursor-pointer min-h-[56px] flex items-center justify-center">
                    <i className="ri-phone-line mr-2"></i>Call for Details
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* International Biometric FAQs */}
          <div className="max-w-4xl mx-auto mb-12 sm:mb-16">
            <div className="text-center mb-8">
              <h2 className="text-xl sm:text-3xl font-bold text-white mb-2">International Biometric Standards</h2>
              <p className="text-gray-300 text-sm sm:text-base">Common questions about Canadian, UK &amp; Australian passport photos</p>
            </div>
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {internationalFAQs.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 last:border-b-0">
                  <button
                    onClick={() => setOpenInternationalFAQ(openInternationalFAQ === index ? null : index)}
                    className="w-full py-4 sm:py-5 px-4 sm:px-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer min-h-[64px] sm:min-h-[72px]"
                    aria-expanded={openInternationalFAQ === index}
                  >
                    <h3 className="text-sm sm:text-lg font-bold text-[#0A1F44] pr-3 sm:pr-4">{faq.question}</h3>
                    <div className={`w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center flex-shrink-0 rounded-full ${openInternationalFAQ === index ? 'bg-[#DC143C]' : 'bg-gray-100'} transition-colors`}>
                      <i className={`ri-arrow-down-s-line text-lg sm:text-xl ${openInternationalFAQ === index ? 'text-white rotate-180' : 'text-gray-600'} transition-transform duration-300`}></i>
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openInternationalFAQ === index ? 'max-h-[500px] pb-4 sm:pb-6' : 'max-h-0'}`}>
                    <div className="px-4 sm:px-6">
                      <p className="text-[#DC143C] font-semibold mb-2 sm:mb-3 text-xs sm:text-base">{faq.snippet}</p>
                      <p className="text-gray-700 leading-relaxed text-xs sm:text-base">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pro-Tip Callout */}
          <div className="max-w-4xl mx-auto mb-12 sm:mb-16">
            <div className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-12 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
              
              <div className="relative">
                <div className="flex items-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#0A1F44] rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                    <i className="ri-lightbulb-flash-line text-[#FFD700] text-xl sm:text-2xl"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0A1F44]">Expert Advice: What to Wear</h3>
                </div>
                
                <p className="text-sm sm:text-lg text-[#0A1F44] leading-relaxed mb-6">
                  To ensure biometric success, please <strong className="text-[#DC143C]">do not wear white</strong>. Dark, solid colors provide the necessary contrast against international white backgrounds to prevent <strong>&apos;floating head&apos; rejections</strong>.
                </p>

                <div className="flex flex-wrap gap-3 items-center">
                  <span className="text-xs sm:text-sm font-bold text-[#0A1F44]">Recommended Colors:</span>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#1a1a2e] border-3 border-white shadow-lg"></div>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#0f4c75] border-3 border-white shadow-lg"></div>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#7c2d12] border-3 border-white shadow-lg"></div>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#14532d] border-3 border-white shadow-lg"></div>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#4c1d95] border-3 border-white shadow-lg"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Local Trust & CTA */}
          <div id="storefront" className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-5xl mx-auto overflow-hidden">
            <div className="grid md:grid-cols-2">
              {/* Image - Left Side */}
              <div className="w-full h-56 sm:h-64 md:h-auto md:min-h-[280px]">
                <img 
                  src="https://static.readdy.ai/image/6521ef01a44cd5c540b1d9b66db907e8/1efa11891abe7a5986b39c318e8c1db9.jpeg"
                  alt="Roberts Photo Studio storefront in Dorchester, Boston"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Text - Right Side */}
              <div className="relative w-full h-auto min-h-[380px] sm:min-h-[400px] md:min-h-[280px] overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/6521ef01a44cd5c540b1d9b66db907e8/c7ee92f2405046e3743a3dacc9c6b5c8.jpeg"
                  alt="Map of Boston neighborhoods"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-white/20"></div>
                <div className="relative z-10 flex items-center justify-center h-full p-4 sm:p-8 md:p-10">
                  <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-2xl p-5 sm:p-8 shadow-2xl flex flex-col items-center justify-center text-center max-w-md">
                    <div className="mb-3 sm:mb-4">
                      <i className="ri-map-pin-line text-[#FFD700] text-4xl sm:text-5xl"></i>
                    </div>
                    <h3 className="text-lg sm:text-2xl md:text-3xl font-bold text-[#0A1F44] mb-2 sm:mb-3">
                      Proudly Serving Greater Boston Community
                    </h3>
                    <p className="text-sm sm:text-base text-[#DC143C] font-bold mb-2 sm:mb-3">
                      840 Morton Street, Boston, MA 02126
                    </p>
                    <p className="text-xs sm:text-base lg:text-lg text-gray-700 mb-4 sm:mb-5">
                      Dorchester • Mattapan • Roxbury • Newton • Milton • Braintree • South Shore • South Boston • East Boston • Charlestown • Back Bay • Allston
                    </p>
                    <a 
                      href="https://www.google.com/maps/place/840+Morton+St,+Boston,+MA+02126"
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#DC143C] text-[#FFD700] px-5 py-3 rounded-full text-sm font-bold hover:bg-[#B71C1C] transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer min-h-[48px]"
                    >
                      <i className="ri-map-pin-line text-lg"></i>
                      Get Directions
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Grid */}
      <section id="pricing" className="py-12 sm:py-16 md:py-20 bg-[#FFD700]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="mb-8 sm:mb-12 md:mb-16 text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#DC143C] mb-4">Studio Pricing</h2>
            <p className="text-base sm:text-xl text-gray-600 font-bold">Professional quality, transparent rates</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-8 max-w-6xl mx-auto">
            {/* Standard US */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-lg relative border-t-4 border-[#DC143C] justify-center">
              
              <div className="text-center mb-5 sm:mb-8">
                <h3 className="text-lg sm:text-2xl font-bold text-black mb-3 sm:mb-4">Standard US Passport</h3>
                <div className="text-4xl sm:text-6xl font-black text-[#DC143C] mb-2">$30</div>
                <p className="text-sm sm:text-base text-gray-600">Includes 2 printed photos</p>
              </div>

              <ul className="space-y-3 mb-5 sm:mb-8">
                <li className="flex items-center text-gray-700 text-sm sm:text-base">
                  <i className="ri-check-line text-green-500 text-lg sm:text-xl mr-3 flex-shrink-0"></i>
                  Professional studio lighting
                </li>
                <li className="flex items-center text-gray-700 text-sm sm:text-base">
                  <i className="ri-check-line text-green-500 text-lg sm:text-xl mr-3 flex-shrink-0"></i>
                  Guaranteed USPS acceptance
                </li>
                <li className="flex items-center text-gray-700 text-sm sm:text-base">
                  <i className="ri-check-line text-green-500 text-lg sm:text-xl mr-3 flex-shrink-0"></i>
                  Same-day service
                </li>
              </ul>

              <a href="#book" className="w-full bg-black text-white py-4 rounded-full text-center block whitespace-nowrap text-sm sm:text-base min-h-[56px] flex items-center justify-center">
                Book Now
              </a>
            </div>

            {/* International/Biometric */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-lg border-t-4 border-[#DC143C] justify-center">
              <div className="text-center mb-5 sm:mb-8">
                <h3 className="text-lg sm:text-2xl font-bold text-black mb-3 sm:mb-4">International &amp; Biometric</h3>
                <div className="text-4xl sm:text-6xl font-black text-[#DC143C] mb-2">$50</div>
                <p className="text-sm sm:text-base text-gray-600">Includes 2 printed photos</p>
              </div>

              <ul className="space-y-3 mb-5 sm:mb-8">
                <li className="flex items-center text-gray-700 text-sm sm:text-base">
                  <i className="ri-check-line text-green-500 text-lg sm:text-xl mr-3 flex-shrink-0"></i>
                  Jamaica, Haiti, Barbados, UK, Canada specs
                </li>
                <li className="flex items-center text-gray-700 text-sm sm:text-base">
                  <i className="ri-check-line text-green-500 text-lg sm:text-xl mr-3 flex-shrink-0"></i>
                  Biometric precision standards
                </li>
                <li className="flex items-center text-gray-700 text-sm sm:text-base">
                  <i className="ri-check-line text-green-500 text-lg sm:text-xl mr-3 flex-shrink-0"></i>
                  Expert compliance review
                </li>
              </ul>

              <a href="#book" className="w-full bg-[#DC143C] text-[#FFD700] py-4 rounded-full font-bold hover:bg-[#B71C1C] transition-colors duration-300 text-center block whitespace-nowrap text-sm sm:text-base min-h-[56px] flex items-center justify-center">
                Book Now
              </a>
            </div>
          </div>

          <div className="text-center mt-6 sm:mt-12 px-4">
            <p className="text-gray-600 text-sm sm:text-lg italic">
              All packages include acceptance guarantee. A digital file of your photos can be obtained for a $75.00 fee.
            </p>
          </div>
        </div>
      </section>

      {/* Appointment Booking Section */}
      <section id="book" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-[#0A1F44] to-[#1a3a5c] relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFD700]/10 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#DC143C]/10 rounded-full -ml-24 -mb-24"></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-14">
            <div className="mb-4">
              <span className="text-[#FFD700] text-[10px] sm:text-sm font-bold uppercase tracking-widest bg-white/10 px-3 sm:px-4 py-2 rounded-full backdrop-blur-sm">
                Schedule Your Session
              </span>
            </div>
            <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 px-2 leading-tight">
              Book Your <span className="text-[#FFD700]">Studio Appointment</span>
            </h2>
            <p className="text-sm sm:text-lg md:text-xl text-gray-200 max-w-2xl mx-auto">
              Reserve your spot for professional passport photos. Walk-ins welcome, but appointments guarantee no wait time.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:grid lg:grid-cols-[340px_1fr] gap-6">
              {/* What to Expect Card */}
              <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-2xl h-fit order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-5 sm:mb-6">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-[#DC143C] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-[#FFD700] text-2xl sm:text-3xl"></i>
                  </div>
                  <h3 className="text-lg sm:text-2xl font-bold text-[#0A1F44]">What to Expect</h3>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-5">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#0A1F44] font-black text-xs sm:text-sm">1</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0A1F44] text-xs sm:text-sm mb-1">Pick a Date &amp; Time</h4>
                      <p className="text-gray-600 text-[10px] sm:text-xs leading-relaxed">Tue, Thu &amp; Sat, 11 AM – 5 PM.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#0A1F44] font-black text-xs sm:text-sm">2</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0A1F44] text-xs sm:text-sm mb-1">Confirm Your Details</h4>
                      <p className="text-gray-600 text-[10px] sm:text-xs leading-relaxed">Name, email, phone &amp; country.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#0A1F44] font-black text-xs sm:text-sm">3</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0A1F44] text-xs sm:text-sm mb-1">Visit Our Studio</h4>
                      <p className="text-gray-600 text-[10px] sm:text-xs leading-relaxed">840 Morton St, 10–15 min session.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#0A1F44] font-black text-xs sm:text-sm">4</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0A1F44] text-xs sm:text-sm mb-1">Get Your Photos</h4>
                      <p className="text-gray-600 text-[10px] sm:text-xs leading-relaxed">Printed photos + optional digital.</p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 bg-[#FAF6F1] rounded-xl p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <i className="ri-shield-check-line text-[#DC143C] text-base sm:text-lg"></i>
                    <span className="text-[#0A1F44] font-bold text-xs">100% Acceptance Guarantee</span>
                  </div>
                  <p className="text-gray-600 text-[10px] sm:text-xs leading-relaxed">Every photo we produce is backed by our ironclad guarantee. If rejected, we reshoot for free.</p>
                </div>
              </div>

              {/* Cal.com Embed Card */}
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden min-h-[500px] sm:min-h-[600px] order-1 lg:order-2">
                <div className="w-full h-full overflow-auto" id="my-cal-inline-passport-photo-appointment" style={{ minHeight: '500px' }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-[#FAF6F1] relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-60"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/6521ef01a44cd5c540b1d9b66db907e8/971e327f7cabb378253648a1bf7903c8.jpeg')`
          }}
        ></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#0A1F44] mb-4 px-2">
              Real Stories from Boston Clients
            </h2>
            <p className="text-sm sm:text-xl text-gray-600">Verified Google Reviews • Trusted Local Expertise</p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            {(() => {
              const cardStyles = [
                { bg: 'bg-gradient-to-br from-[#0A1F44] to-[#1a3a5c]', text: 'text-white', subtext: 'text-gray-300', nameText: 'text-white', tagText: 'text-gray-400', border: 'border-[#FFD700]/30', quoteColor: 'text-[#FFD700]', serviceTagBg: 'bg-[#DC143C]', serviceTagText: 'text-[#FFD700]', divider: 'border-white/20' },
                { bg: 'bg-gradient-to-br from-[#DC143C] to-[#B71C1C]', text: 'text-white', subtext: 'text-red-100', nameText: 'text-white', tagText: 'text-red-200', border: 'border-[#FFD700]/30', quoteColor: 'text-[#FFD700]', serviceTagBg: 'bg-[#0A1F44]', serviceTagText: 'text-[#FFD700]', divider: 'border-white/20' },
                { bg: 'bg-gradient-to-br from-[#FFD700] to-[#FFA500]', text: 'text-[#0A1F44]', subtext: 'text-[#0A1F44]/70', nameText: 'text-[#0A1F44]', tagText: 'text-[#0A1F44]/60', border: 'border-[#0A1F44]/20', quoteColor: 'text-[#DC143C]', serviceTagBg: 'bg-[#0A1F44]', serviceTagText: 'text-[#FFD700]', divider: 'border-[#0A1F44]/20' },
              ];
              const style = cardStyles[currentTestimonial];
              return (
                <div className={`${style.bg} rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-12 shadow-2xl border ${style.border} transition-all duration-500`}>
                  {/* Avatar Circle */}
                  <div className="flex justify-center mb-4 sm:mb-6">
                    <div className="w-16 h-16 sm:w-24 sm:h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg ring-4 ring-white/30">
                      <span className={`text-xl sm:text-3xl font-bold ${style.nameText}`}>
                        {testimonials[currentTestimonial].initials}
                      </span>
                    </div>
                  </div>

                  {/* Service Tag */}
                  <div className="text-center mb-3 sm:mb-4">
                    <span className={`inline-block ${style.serviceTagBg} ${style.serviceTagText} px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-sm font-bold uppercase tracking-wide`}>
                      {testimonials[currentTestimonial].service}
                    </span>
                  </div>

                  {/* Star Rating */}
                  <div className="flex justify-center items-center gap-1 sm:gap-2 mb-4 sm:mb-6">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-[#FFD700] text-lg sm:text-2xl drop-shadow-md"></i>
                    ))}
                    <div className="ml-2 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-google-fill text-lg sm:text-xl text-white/80"></i>
                    </div>
                  </div>

                  {/* Review Text */}
                  <div className="mb-6 sm:mb-8">
                    <div className={`text-4xl sm:text-6xl ${style.quoteColor} mb-2 sm:mb-4 font-serif text-center`}>&ldquo;</div>
                    <p className={`text-sm sm:text-lg md:text-xl ${style.text} leading-relaxed font-serif italic text-center max-w-3xl mx-auto`}>
                      {testimonials[currentTestimonial].review}
                    </p>
                  </div>

                  {/* Customer Name & Tag */}
                  <div className={`text-center border-t ${style.divider} pt-4 sm:pt-6`}>
                    <h4 className={`text-base sm:text-xl font-bold ${style.nameText} mb-1 sm:mb-2`}>
                      {testimonials[currentTestimonial].name}
                    </h4>
                    <p className={`text-xs sm:text-sm ${style.tagText} font-medium`}>
                      {testimonials[currentTestimonial].tag}
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Navigation Arrows - Below card on mobile, sides on desktop */}
            <div className="flex justify-center gap-4 mt-6 sm:mt-0">
              <button
                onClick={prevTestimonial}
                className="sm:absolute sm:left-0 sm:top-1/2 sm:-translate-y-1/2 sm:-translate-x-1/2 md:-translate-x-full md:mr-4 w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full shadow-xl flex items-center justify-center hover:bg-[#FFD700] transition-all duration-300 z-10 cursor-pointer"
                aria-label="Previous testimonial"
              >
                <i className="ri-arrow-left-s-line text-2xl sm:text-3xl text-[#0A1F44]"></i>
              </button>
              <button
                onClick={nextTestimonial}
                className="sm:absolute sm:right-0 sm:top-1/2 sm:-translate-y-1/2 sm:translate-x-1/2 md:translate-x-full md:ml-4 w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full shadow-xl flex items-center justify-center hover:bg-[#FFD700] transition-all duration-300 z-10 cursor-pointer"
                aria-label="Next testimonial"
              >
                <i className="ri-arrow-right-s-line text-2xl sm:text-3xl text-[#0A1F44]"></i>
              </button>
            </div>

            {/* Dot indicators for mobile */}
            <div className="flex justify-center gap-2 mt-4 sm:hidden">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${currentTestimonial === index ? 'bg-[#DC143C] w-6' : 'bg-gray-300'}`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* CTA Below Carousel */}
          <div className="text-center mt-8 sm:mt-12">
            <a 
              href="https://www.google.com/search?sca_esv=67f5f285935010ba&sxsrf=ANbL-n6PYVEFIEW-fl0Kh1EHDvMlclmlDg:1770586580484&si=AL3DRZFIhG6pAqfNLal55wUTwygCG0fClF3UxiOmgw9Hq7nbWVM102TDtU1Xm29pfc5bNY5U3qjvqiJhISyMOzTeGujw2YLekxvGUCBy1ytGU665rkGZBrimA31VoG8lqOUhqkoz4TrRQH8JQjCHGa7xiAq4AotUMg%3D%3D&q=Roberts+Photo+Studio+Reviews&sa=X&ved=2ahUKEwjr1bGB7cqSAxW8lmoFHQDMFXUQ0bkNegQIQBAH&biw=1166&bih=910&dpr=1#topic=mid:AgAMLPi3-YU" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white border-2 border-[#0A1F44] text-[#0A1F44] px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-lg font-bold hover:bg-[#0A1F44] hover:text-white transition-all duration-300 shadow-lg whitespace-nowrap min-h-[48px]"
            >
              <i className="ri-google-fill text-lg sm:text-xl"></i>
              Read All Google Reviews
            </a>
          </div>
        </div>
      </section>

      {/* Quick Answers Section - Before Footer */}
      <section className="py-12 sm:py-16 md:py-20 bg-[#FAF6F1]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-14">
            <div className="mb-4">
              <span className="text-[#DC143C] text-xs sm:text-sm font-bold uppercase tracking-widest bg-white px-4 py-2 rounded-full">
                Your Questions Answered
              </span>
            </div>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#0A1F44] mb-4">
              Quick Answers
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Why Boston families have trusted Roberts Photo Studio since 1973
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden">
              {quickAnswerFAQs.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 last:border-b-0">
                  <button
                    onClick={() => setOpenQuickFAQ(openQuickFAQ === index ? null : index)}
                    className="w-full py-4 sm:py-5 px-4 sm:px-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer min-h-[64px] sm:min-h-[72px]"
                    aria-expanded={openQuickFAQ === index}
                  >
                    <h3 className="text-sm sm:text-lg font-bold text-[#0A1F44] pr-3 sm:pr-4">{faq.question}</h3>
                    <div className={`w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center flex-shrink-0 rounded-full ${openQuickFAQ === index ? 'bg-[#DC143C]' : 'bg-gray-100'} transition-colors`}>
                      <i className={`ri-arrow-down-s-line text-lg sm:text-xl ${openQuickFAQ === index ? 'text-white rotate-180' : 'text-gray-600'} transition-transform duration-300`}></i>
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openQuickFAQ === index ? 'max-h-[500px] pb-4 sm:pb-6' : 'max-h-0'}`}>
                    <div className="px-4 sm:px-6">
                      <p className="text-[#DC143C] font-semibold mb-2 sm:mb-3 text-xs sm:text-base">{faq.snippet}</p>
                      <p className="text-gray-700 leading-relaxed text-xs sm:text-base">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-6 sm:mt-8">
              <Link 
                to="/faq" 
                className="inline-flex items-center gap-2 bg-[#0A1F44] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-lg font-bold hover:bg-[#1a3a5c] transition-all duration-300 shadow-lg whitespace-nowrap min-h-[48px]"
              >
                <i className="ri-question-answer-line text-lg sm:text-xl"></i>
                View All FAQs
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-black py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-12">
            {/* Brand & Social */}
            <div>
              <h3 className="text-xl sm:text-3xl md:text-4xl font-black text-red-600 mb-3 sm:mb-6">Roberts Photo Studio</h3>
              <p className="text-[#FAF6F1] text-sm sm:text-lg mb-4 sm:mb-8">Your passport to confident travel portraits</p>
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap gap-4 sm:gap-5">
                  <a href="https://www.instagram.com/desieroberts/" target="_blank" rel="noopener noreferrer" className="w-11 h-11 sm:w-12 sm:h-12 border-2 border-white rounded-full flex items-center justify-center text-white hover:bg-[#40E0D0] hover:border-[#40E0D0] transition-colors">
                    <i className="ri-instagram-line text-lg sm:text-xl"></i>
                  </a>
                  <a href="https://www.facebook.com/robertsphotostudioboston" target="_blank" rel="noopener noreferrer" className="w-11 h-11 sm:w-12 sm:h-12 border-2 border-white rounded-full flex items-center justify-center text-white hover:bg-[#40E0D0] hover:border-[#40E0D0] transition-colors">
                    <i className="ri-facebook-line text-lg sm:text-xl"></i>
                  </a>
                </div>
                <a 
                  href="https://www.robertsphotostudio.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#DC143C] text-[#FFD700] px-5 py-3 rounded-full text-sm font-bold hover:bg-[#B71C1C] transition-colors whitespace-nowrap cursor-pointer w-fit min-h-[48px]"
                >
                  <i className="ri-external-link-line"></i>
                  View All Our Services
                </a>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <div className="space-y-5 sm:space-y-8">
                <div>
                  <h4 className="text-[#FFD700] text-xs uppercase font-bold tracking-widest mb-2">Studio Location</h4>
                  <p className="text-white text-sm sm:text-lg">840 Morton Street</p>
                  <p className="text-white text-sm sm:text-lg">Boston, MA 02126</p>
                </div>
                <div>
                  <h4 className="text-[#FFD700] text-xs uppercase font-bold tracking-widest mb-2">Studio Hours</h4>
                  <p className="text-white text-sm sm:text-lg">Tuesday, Thursday, Saturday</p>
                  <p className="text-white text-sm sm:text-lg">11:00 AM – 5:00 PM</p>
                </div>
                <div>
                  <h4 className="text-[#FFD700] text-xs uppercase font-bold tracking-widest mb-2">Book Your Session</h4>
                  <a href="tel:617-288-4417" className="text-white text-lg sm:text-2xl font-bold hover:text-[#FFD700] transition-colors cursor-pointer min-h-[48px] inline-flex items-center">
                    <i className="ri-phone-line mr-2"></i>(617) 288-4417
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links & Studio Image */}
            <div>
              <h4 className="text-white text-sm sm:text-lg font-bold mb-3 sm:mb-4">Quick Links</h4>
              <div className="space-y-1 sm:space-y-3">
                <a href="#caribbean" className="text-white hover:text-[#FFD700] transition-colors text-sm sm:text-base min-h-[48px] flex items-center">International Passports</a>
                <a href="#pricing" className="text-white hover:text-[#FFD700] transition-colors text-sm sm:text-base min-h-[48px] flex items-center">Pricing</a>
                <Link to="/faq" className="text-white hover:text-[#FFD700] transition-colors text-sm sm:text-base min-h-[48px] flex items-center">FAQs</Link>
                <a href="#book" className="text-white hover:text-[#FFD700] transition-colors text-sm sm:text-base min-h-[48px] flex items-center">Book Online</a>
                <a href="#storefront" className="text-white hover:text-[#FFD700] transition-colors text-sm sm:text-base min-h-[48px] flex items-center">
                  <i className="ri-store-2-line mr-2"></i>See Our Storefront
                </a>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-white/50 pt-6 sm:pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
              <p className="text-white text-xs sm:text-sm text-center md:text-left">© 2026 Roberts Photo Studio. All rights reserved.</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="https://readdy.ai/?ref=logo" className="text-white text-xs hover:text-[#FFD700] transition-colors min-h-[44px] flex items-center">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
